import { Check, Lock, LockOpen } from "lucide-react";
import { useMemo, useState } from "react";
import type { Matter } from "@/shared/cache";
import { getRecentMatters, searchMatters } from "@/shared/utils/matterSearch";
import { renderMatterItem } from "@/v2/components/entry-form/pickerItems";
import { Button } from "@/v2/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/v2/components/ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/v2/components/ui/tooltip";
import { cn } from "@/v2/lib/utils";

interface MatterLockButtonProps {
	matters: Matter[];
	/** Hardcoded matter_id ("" = none) */
	value: string;
	onChange: (matterId: string) => void;
}

const MAX_RESULTS = 50;

/**
 * Compact lock button for hardcoding a matter on the v2 entries page.
 * When a matter is locked, all new entries default to it.
 */
export function MatterLockButton({
	matters,
	value,
	onChange,
}: MatterLockButtonProps) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const isLocked = !!value;
	const selected = matters.find((m) => m.matter_id === value) ?? null;

	const results = useMemo(() => {
		const list = query.trim()
			? searchMatters(matters, query)
			: getRecentMatters(matters);
		return list.slice(0, MAX_RESULTS);
	}, [matters, query]);

	const handleOpenChange = (next: boolean) => {
		setOpen(next);
		if (!next) setQuery("");
	};

	const handleSelect = (matterId: string) => {
		onChange(matterId);
		handleOpenChange(false);
	};

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								size="icon"
								className={cn(
									"relative",
									isLocked &&
										"border-ring text-ring hover:border-ring hover:text-ring",
								)}
								aria-label="Hardcode a matter"
							>
								{isLocked ? <Lock /> : <LockOpen />}
								{isLocked && (
									<span className="absolute right-1 top-1 size-1.5 rounded-full bg-ring" />
								)}
							</Button>
						</PopoverTrigger>
					</TooltipTrigger>
					<TooltipContent side="bottom" className="max-w-xs">
						{selected
							? `Locked: ${selected.matter.trim()} — ${selected.description.trim()}`
							: "Hardcode a matter — new entries use it"}
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<PopoverContent className="w-[340px] p-0" align="start">
				<input
					type="text"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder="Search matter..."
					className="w-full border-b border-border bg-transparent px-3 py-2.5 text-sm text-foreground outline-none placeholder:text-muted-foreground"
				/>

				<button
					type="button"
					onClick={() => handleSelect("")}
					className="flex w-full items-center px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
				>
					<Check
						className={cn(
							"mr-2 size-4 shrink-0",
							value === "" ? "opacity-100" : "opacity-0",
						)}
					/>
					None
				</button>
				<div className="h-px bg-border" />

				<ul className="max-h-[300px] overflow-y-auto py-1">
					{results.length === 0 ? (
						<li className="px-3 py-6 text-center text-sm text-muted-foreground">
							No matter found.
						</li>
					) : (
						results.map((matter) => {
							const isSelected = matter.matter_id === value;
							return (
								<li key={matter.matter_id}>
									<button
										type="button"
										onClick={() => handleSelect(matter.matter_id)}
										className={cn(
											"flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-accent hover:text-accent-foreground",
											isSelected && "bg-accent/50",
										)}
									>
										<Check
											className={cn(
												"size-4 shrink-0",
												isSelected ? "opacity-100" : "opacity-0",
											)}
										/>
										<div className="min-w-0 flex-1">
											{renderMatterItem(matter)}
										</div>
									</button>
								</li>
							);
						})
					)}
				</ul>
			</PopoverContent>
		</Popover>
	);
}
